import { Box, Typography, MenuItem, Select, Grid2 } from "@mui/material";
import { useMemo, useState } from "react";
import SRATable from "../SRATable";

const StatisticsPanel = ({ sras }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formType, setFormType] = useState("ALL");
  const [activeTab, setActiveTab] = useState("ALL");

  const filterByDateRange = (data) => {
    if (!startDate && !endDate) return data;

    return data.filter((item) => {
      const created = new Date(item.createdDate);

      if (startDate && created < new Date(startDate)) return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (created > end) return false;
      }

      return true;
    });
  };

  const getFormName = (formId, writeType) => {
    if (formId.includes("SO")) return "SERVICE";
    if (formId.includes("IA")) return "INVOICE";
    if (formId.includes("CA")) return "CUSTOMER";
    if (formId.includes("VA")) return "VENDOR_APPROVAL";
    if (formId.includes("TM")) return "TRADE";
    if (formId.includes("VAD")) return "VENDOR_ADVANCE";
    if (formId.includes("CD")) return "CUSTOMER_DEPOSIT";
    if (formId.includes("TD")) return "TENDER_DEPOSIT";
    if (formId.includes("WOVB") && writeType === "Off") return "VENDOR-Off";
    if (formId.includes("WOCB") && writeType === "Off")
      return "CUSTOMER_BALANCE-Off";
    if (formId.includes("WOAG") && writeType === "Off") return "ADVANCE-Off";
    if (formId.includes("WODG") && writeType === "Off") return "DEPOSIT-Off";
    if (formId.includes("WOI") && writeType === "Off") return "INVENTORY-Off";
    if (formId.includes("WOO") && writeType === "Off") return "OTHER-Off";
    if (formId.includes("WBVB") && writeType === "Back") return "VENDOR-Back";
    if (formId.includes("WBCB") && writeType === "Back")
      return "CUSTOMER_BALANCE-Back";
    if (formId.includes("WBAG") && writeType === "Back") return "ADVANCE-Back";
    if (formId.includes("WBDG") && writeType === "Back") return "DEPOSIT-Back";
    if (formId.includes("WBI") && writeType === "Back") return "INVENTORY-Back";
    if (formId.includes("WBO") && writeType === "Back") return "OTHER-Back";

    return null;
  };

  const filtered = useMemo(() => {
    let data = [...sras];

    // Duration filter
    data = filterByDateRange(data);

    // Form type filter
    if (formType !== "ALL") {
      data = data.filter(
        (item) => getFormName(item.id, item.writeType) === formType
      );
    }

    return data;
  }, [startDate, endDate, formType, sras]);

  const filterByStatus = (data) => {
    if (activeTab === "ALL") return data;

    if (activeTab === "APPROVED") {
      return data.filter(
        (s) => s.status === "APPROVED" || s.status === "PROCEEDED"
      );
    }

    if (activeTab === "REJECTED") {
      return data.filter((s) => s.status === "REJECTED");
    }

    if (activeTab === "PENDING") {
      return data.filter((s) => s.status === "PENDING" || s.status === "QUERY");
    }

    return data;
  };

  const finalData = useMemo(() => {
    return filterByStatus(filtered);
  }, [filtered, activeTab]);

  const total = filtered.length;
  const approved = filtered.filter(
    (s) => s.status === "APPROVED" || s.status === "PROCEEDED"
  ).length;
  const rejected = filtered.filter((s) => s.status === "REJECTED").length;
  const inProcess = filtered.filter(
    (s) => s.status === "PENDING" || s.status === "QUERY"
  ).length;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <div style={{ flex: 1 }}>
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div style={{ flex: 1 }}>
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="">Form Name</label>
          <select
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
          >
            <option value="ALL">All</option>
            <option value="SERVICE">Service Order Approval</option>
            <option value="INVOICE">Invoice Approval</option>
            <option value="CUSTOMER">New Customer Approval</option>
            <option value="VENDOR_APPROVAL">New Vendor Approval</option>
            <option value="TRADE">Trade Marketing Scheme</option>
            <option value="VENDOR_ADVANCE">Vendor Advance</option>
            <option value="CUSTOMER_DEPOSIT">Customer Deposit</option>
            <option value="TENDER_DEPOSIT">Tender Deposit</option>
            <option value="VENDOR-Off">Write Off - Vendor Balances</option>
            <option value="CUSTOMER_BALANCE-Off">
              Write Off - Customer Balances
            </option>
            <option value="ADVANCE-Off">Write Off - Advances Given</option>
            <option value="DEPOSIT-Off">Write Off - Deposit Given</option>
            <option value="INVENTORY-Off">Write Off - Inventory</option>
            <option value="OTHER-Off">Write Off - Others</option>

            <option value="VENDOR-Back">Write Back - Vendor Balances</option>
            <option value="CUSTOMER_BALANCE-Back">
              Write Back - Customer Balances
            </option>
            <option value="ADVANCE-Back">Write Back - Advances Given</option>
            <option value="DEPOSIT-Back">Write Back - Deposit Given</option>
            <option value="INVENTORY-Back">Write Back - Inventory</option>
            <option value="OTHER-Back">Write Back - Others</option>
          </select>
        </div>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Statistics
        </Typography>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 6, sm: 3 }}>
            <Box
              textAlign="center"
              onClick={() => setActiveTab("ALL")}
              sx={{
                cursor: "pointer",
                borderBottom:
                  activeTab === "ALL" ? "2px solid #1976d2" : "none",
              }}
            >
              <Typography sx={{ fontSize: "32px", fontWeight: 700 }}>
                {total}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "gray" }}>
                Total Forms
              </Typography>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 3 }}>
            <Box
              textAlign="center"
              onClick={() => setActiveTab("APPROVED")}
              sx={{
                cursor: "pointer",
                borderBottom:
                  activeTab === "APPROVED" ? "2px solid #1976d2" : "none",
              }}
            >
              <Typography sx={{ fontSize: "32px", fontWeight: 700 }}>
                {approved}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "gray" }}>
                Approved
              </Typography>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 3 }}>
            <Box
              textAlign="center"
              onClick={() => setActiveTab("REJECTED")}
              sx={{
                cursor: "pointer",
                borderBottom:
                  activeTab === "REJECTED" ? "2px solid #1976d2" : "none",
              }}
            >
              <Typography sx={{ fontSize: "32px", fontWeight: 700 }}>
                {rejected}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "gray" }}>
                Rejected
              </Typography>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 3 }}>
            <Box
              textAlign="center"
              onClick={() => setActiveTab("PENDING")}
              sx={{
                cursor: "pointer",
                borderBottom:
                  activeTab === "PENDING" ? "2px solid #1976d2" : "none",
              }}
            >
              <Typography sx={{ fontSize: "32px", fontWeight: 700 }}>
                {inProcess}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "gray" }}>
                In Process
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <SRATable sras={finalData} activeTab={"STATS"} />
    </Box>
  );
};

export default StatisticsPanel;
